import { showUserAlert } from "../utils/userErrorMessage";
// src/pages/BusHubDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

function Badge({ status }) {
  const base = "px-2 py-0.5 rounded text-xs font-semibold";
  switch (status) {
    case "open": return <span className={`${base} bg-emerald-100 text-emerald-700`}>모집중</span>;
    case "accepted": return <span className={`${base} bg-blue-100 text-blue-700`}>선정됨</span>;
    case "payment_held": return <span className={`${base} bg-amber-100 text-amber-700`}>결제 보류(PG)</span>;
    case "in_progress": return <span className={`${base} bg-indigo-100 text-indigo-700`}>진행중</span>;
    case "cancelled": return <span className={`${base} bg-red-100 text-red-700`}>취소</span>;
    case "settled": return <span className={`${base} bg-gray-200 text-gray-700`}>정산완료</span>;
    default: return <span className={`${base} bg-gray-100 text-gray-600`}>상태없음</span>;
  }
}

function fmtKST(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("ko-KR", {
    timeZone: "Asia/Seoul",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getStoredEmail() {
  return (
    localStorage.getItem("email") ||
    localStorage.getItem("userEmail") ||
    ""
  ).trim();
}

export default function BusHubDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  // 로그인 사용자
  const [user, setUser] = useState(null);
  const [actorEmail, setActorEmail] = useState(getStoredEmail());
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user || null);
      setActorEmail((user?.email || getStoredEmail()).trim());
    });
  }, []);

  const myRole = localStorage.getItem("role") || ""; // first/operator 접근 가정

  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [apps, setApps] = useState([]); // 신청 목록
  const [busy, setBusy] = useState(false);
  const [applyQty, setApplyQty] = useState(1);

  const isAuthor = useMemo(
    () => !!(post && ((user?.id && post.author_id === user.id) || post.author === actorEmail)),
    [actorEmail, post, user]
  );
  const alreadyApplied = useMemo(
    () =>
      apps.some(
        (a) =>
          ((user?.id && a.applicant_id === user.id) || a.applicant_email === actorEmail) &&
          a.status !== "cancelled"
      ),
    [actorEmail, apps, user]
  );

  // 남은 필요대수 계산 (수락된 합계 기준)
  const acceptedSum = useMemo(
    () =>
      apps.reduce((sum, a) => {
        if (a.status !== "accepted") return sum;
        const qty =
          (a.accepted_qty && a.accepted_qty > 0)
            ? a.accepted_qty
            : (a.requested_qty || 1);
        return sum + qty;
      }, 0),
    [apps]
  );
  const required = post?.car_count || 0;
  const remaining = Math.max(0, required - acceptedSum);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const currentEmail = (currentUser?.email || getStoredEmail()).trim();
      setUser(currentUser || null);
      setActorEmail(currentEmail);
      if (!currentEmail) throw new Error("로그인 정보가 필요합니다.");

      // 게시글
      const { data: p, error: pErr } = await supabase
        .from("bus_hub_posts")
        .select("*")
        .eq("id", id)
        .single();
      if (pErr) throw pErr;
      setPost(p);

      const { error: viewErr } = await supabase.rpc("bus_hub_increment_views", { p_post_id: id });
      if (viewErr) {
        console.warn("bus hub view increment failed:", viewErr.message);
      }

      const author = (currentUser?.id && p.author_id === currentUser.id) || p.author === currentEmail;
      if (author) {
        const { data: a, error: aErr } = await supabase
          .from("bus_hub_applications")
          .select("*")
          .eq("post_id", id)
          .order("created_at", { ascending: false });
        if (aErr) {
          console.warn("bus hub applications fetch failed:", aErr.message);
          setApps([]);
          return;
        }
        setApps(a || []);
      } else {
        const { data: mine, error: mineErr } = await supabase
          .from("bus_hub_applications")
          .select("*")
          .eq("post_id", id)
          .eq(currentUser?.id ? "applicant_id" : "applicant_email", currentUser?.id || currentEmail)
          .order("created_at", { ascending: false });
        if (mineErr) {
          console.warn("bus hub my application fetch failed:", mineErr.message);
          setApps([]);
          return;
        }
        setApps(mine || []);
      }
    } catch (e) {
      console.error("detail fetch error:", e.message);
      showUserAlert("게시글을 불러오지 못했습니다.");
      navigate("/bus-hub");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    // 권한(메뉴 단계에서 이미 차단했겠지만 혹시 몰라서 한번 더)
    if (myRole !== "first" && myRole !== "operator") {
      showUserAlert("접근 권한이 없습니다.");
      navigate("/reservations");
      return;
    }
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleApply = async () => {
    if (!post || busy) return;
    if (isAuthor) return showUserAlert("본인이 올린 글에는 신청할 수 없습니다.");
    if (alreadyApplied) return showUserAlert("이미 신청하셨습니다.");

    setBusy(true);
    try {
      const { data:{ user: u } } = await supabase.auth.getUser();
      if (!u) throw new Error("로그인이 필요합니다.");
      const requestedQty = Math.max(
        1,
        Math.min(Number(applyQty) || 1, Math.max(1, remaining))
      );
      setApplyQty(requestedQty);
      const { error } = await supabase.rpc("bus_hub_apply", {
        p_post_id: id,
        p_requested_qty: requestedQty,
      });
      if (error) throw error;
      showUserAlert("신청이 등록되었습니다.");
      // 작성자가 아닌 경우엔 목록을 다시 안 불러와도 되지만, 상태 갱신을 위해 재조회
      fetchDetail();
    } catch (e) {
      // 23505 = unique_violation
      if (e.code === "23505") {
        showUserAlert("이미 신청하셨습니다.");
      } else {
        showUserAlert("신청 실패: " + e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  // 작성자 전용: 특정 신청을 수락(부분 수락 포함)
  const acceptApp = async (app) => {
    if (!isAuthor || busy) return;
    if (app.status !== "applied") return; // 이미 처리된 건 방지

    // 남은 대수 한도 내에서 수락 (UI 기준으로 계산)
    const qtyToAccept = Math.min(app.requested_qty || 1, remaining);
    if (qtyToAccept <= 0) {
      return showUserAlert("남은 대수가 없습니다.");
    }

    setBusy(true);
    try {
      const { error } = await supabase.rpc("bus_hub_accept_application", {
        p_application_id: app.id,
      });
      if (error) throw error;

      await fetchDetail(); // 새 데이터로 합계/남은 대수 갱신
    } catch (e) {
      showUserAlert("수락 실패: " + e.message);
    } finally {
      setBusy(false);
    }
 };


  // 작성자 전용: 신청 상태 변경 (수락/거절)
  const updateAppStatus = async (appId, next) => {
    if (!isAuthor || busy) return;
    setBusy(true);
    try {
      const { error } =
        next === "rejected"
          ? await supabase.rpc("bus_hub_reject_application", { p_application_id: appId })
          : await supabase.rpc("bus_hub_update_application_status", {
              p_application_id: appId,
              p_status: next,
            });
      if (error) throw error;
      await fetchDetail();
    } catch (e) {
      showUserAlert("상태 변경 실패: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading || !post) {
    return (
      <div className="space-y-4">
        <Header />
        <div className="p-4">불러오는 중...</div>
      </div>
    );
  }

  const period = `${fmtKST(post.start_at) || "-"} ~ ${fmtKST(post.end_at) || "-"}`;
  const content = post.content || post.job_desc || "";
  const displayCompany = post.company_name || post.company || "-";

  return (
    <div className="space-y-4">
      <Header />
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">버스 허브 - 상세</h2>
          <div className="flex items-center gap-2">
            <Badge status={post.status} />
            <span className="text-xs text-gray-500">조회 {post.views ?? 0}</span>
          </div>
        </div>

        <Card className="border-0 shadow-none">
          <CardContent className="bg-white space-y-3 p-4">
            <div className="space-y-1">
              <div className="text-xl font-bold">{post.title}</div>
              <div className="text-sm text-gray-600">
                작성 회사: {displayCompany} · 지역: {post.region} · 차량 대수: {post.car_count}대
              </div>
              <div className="text-sm text-gray-600">기간/시간: {period}</div>
              <div className="text-sm text-gray-800">금액: {(post.price ?? 0).toLocaleString()}원</div>
              <div className="text-sm text-gray-500">등록일: {(post.created_at || "").slice(0, 10)}</div>
            </div>
            <div className="space-y-1 text-sm text-gray-600">
              <div>필요 대수: {required}대</div>
              <div>수락 누계: {acceptedSum}대</div>
              <div>남은 대수: {remaining}대</div>
            </div>

            {content && (
              <div className="border-t pt-3">
                <div className="font-semibold mb-1">일거리 상세</div>
                <div className="whitespace-pre-wrap text-sm">{content}</div>
              </div>
            )}

            <div className="pt-2">
              {/* 작성자면 신청 버튼 숨김, 작성자가 아니고 이미 신청했으면 비활성 */}
              {!isAuthor && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-semibold" htmlFor="bus-hub-apply-qty">
                    신청 대수
                  </label>
                  <Input
                    id="bus-hub-apply-qty"
                    type="number"
                    min={1}
                    max={Math.max(1, remaining)}
                    value={applyQty}
                    onChange={(e) => setApplyQty(Math.max(1, Math.min(+e.target.value || 1, Math.max(1, remaining))))}
                    className="w-24"
                  />
                  <span className="text-sm">대</span>

                  <Button onClick={handleApply} disabled={busy || alreadyApplied || remaining === 0}>
                    {alreadyApplied ? "신청 완료됨" : "이 게시물에 신청"}
                  </Button>
                  <span className="text-xs text-gray-500">남은 필요 대수: {remaining}대</span>
                </div>
              )}
              <Button variant="outline" onClick={() => navigate("/bus-hub")}>
                목록으로
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 작성자 전용: 신청자 목록 */}
        {isAuthor && (
          <Card className="border-0 shadow-none">
            <CardContent className="bg-white p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold">신청자 목록</div>
                <div className="text-sm text-gray-500">총 {apps.length}건</div>
              </div>
              <div className="overflow-x-auto">
                <table className="table-auto w-full border text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border p-2 text-left">신청일시</th>
                      <th className="border p-2 text-left">신청자</th>
                      <th className="border p-2 text-left">상태</th>
                      <th className="border p-2 text-left">요청대수</th> {/* ✅ 추가 */}
                      <th className="border p-2 text-left">액션</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map((a) => (
                      <tr key={a.id}>
                        <td className="border p-2">{(a.created_at || "").replace("T", " ").slice(0, 16)}</td>
                        <td className="border p-2">{a.applicant_email}</td>
                        <td className="border p-2">
                          <Badge status={a.status} />
                        </td>
                        <td className="border p-2">{a.requested_qty ?? 1}대</td> {/* ✅ 추가 */}
                        <td className="border p-2">
                          {a.status === "applied" ? (
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => acceptApp(a)} disabled={busy || remaining === 0}>
                                수락
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateAppStatus(a.id, "rejected")}
                                disabled={busy}
                              >
                                거절
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">처리됨</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {apps.length === 0 && (
                      <tr>
                        <td className="border p-4 text-center text-gray-500" colSpan={5}>
                          신청자가 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* 추후 결제/보류/정산은 별도 관리 페이지에서 진행하도록 버튼만 예시 */}
              <div className="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/bus-hub/${id}/manage`)}
                >
                  신청/결제 관리로 이동
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
